import { z } from 'zod';

/** Eight DOWNTIME lean waste types (mnemonic: DOWNTIME). */
export const DOWNTIME_WASTE_TYPE_VALUES = [
  'DEFECTS',
  'OVERPRODUCTION',
  'WAITING',
  'NON_UTILIZED_TALENT',
  'TRANSPORTATION',
  'INVENTORY',
  'MOTION',
  'EXTRA_PROCESSING',
] as const;

export type DowntimeWasteType = (typeof DOWNTIME_WASTE_TYPE_VALUES)[number];

export const downtimeWasteTypeSchema = z.enum(DOWNTIME_WASTE_TYPE_VALUES);

export const papercutWasteTypesSchema = z
  .array(downtimeWasteTypeSchema)
  .max(DOWNTIME_WASTE_TYPE_VALUES.length);

export interface DowntimeWasteDefinition {
  type: DowntimeWasteType;
  name: string;
  description: string;
  initial: string;
  color: string;
  bg: string;
}

export const DOWNTIME_WASTE_DEFINITIONS: DowntimeWasteDefinition[] = [
  {
    type: 'DEFECTS',
    name: 'Defects',
    description: 'Errors in products or services that require rework, repair, or scrapping.',
    initial: 'D',
    color: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
  },
  {
    type: 'OVERPRODUCTION',
    name: 'Overproduction',
    description: 'Producing more than is needed or producing too early.',
    initial: 'O',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
  },
  {
    type: 'WAITING',
    name: 'Waiting',
    description: 'Idle time when people or processes are waiting for the next step.',
    initial: 'W',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
  },
  {
    type: 'NON_UTILIZED_TALENT',
    name: 'Non-utilized Talent',
    description: "Underusing people's talents, skills, and knowledge.",
    initial: 'N',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  {
    type: 'TRANSPORTATION',
    name: 'Transportation',
    description: 'Unnecessary movement of products or materials.',
    initial: 'T',
    color: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
  },
  {
    type: 'INVENTORY',
    name: 'Inventory',
    description: 'Excess materials or products not being processed.',
    initial: 'I',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    type: 'MOTION',
    name: 'Motion',
    description: 'Unnecessary movement of people.',
    initial: 'M',
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
  },
  {
    type: 'EXTRA_PROCESSING',
    name: 'Extra Processing',
    description: 'More work or higher quality than is required.',
    initial: 'E',
    color: 'text-violet-600 dark:text-violet-400',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
  },
];

const wasteDefinitionByType = new Map(
  DOWNTIME_WASTE_DEFINITIONS.map((definition) => [definition.type, definition]),
);

export function getDowntimeWasteDefinition(type: DowntimeWasteType): DowntimeWasteDefinition {
  const definition = wasteDefinitionByType.get(type);
  if (!definition) {
    throw new Error(`Unknown downtime waste type: ${type}`);
  }
  return definition;
}

export function getDowntimeWasteLabel(type: DowntimeWasteType): string {
  return getDowntimeWasteDefinition(type).name;
}

export function normalizePapercutWasteTypes(value: unknown): DowntimeWasteType[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const allowed = new Set<string>(DOWNTIME_WASTE_TYPE_VALUES);
  const unique = new Set<DowntimeWasteType>();

  for (const item of value) {
    if (typeof item === 'string' && allowed.has(item)) {
      unique.add(item as DowntimeWasteType);
    }
  }

  return DOWNTIME_WASTE_TYPE_VALUES.filter((type) => unique.has(type));
}

export function formatPapercutWasteTypes(value: unknown): string {
  const types = normalizePapercutWasteTypes(value);
  if (types.length === 0) {
    return 'None';
  }
  return types.map(getDowntimeWasteLabel).join(', ');
}
